import {Component, Input, OnDestroy} from '@angular/core';
import {Opportunity} from '../../interfaces/opportunity.interface';
import {AuthService} from '../../../services/auth.service';
import {FollowUpCreationDialogComponent} from '../follow-up-creation-dialog/follow-up-creation-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {OpportunityStatuses} from '../../interfaces/opportunity-statuses.enum';
import {OpportunityDeletionDialogComponent} from '../opportunity-deletion-dialog/opportunity-deletion-dialog.component';
import {filter, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {OpportunityService} from '../../services/opportunity.service';
import {AssignSalesManagerDialogComponent} from '../assign-sales-manager-dialog/assign-sales-manager-dialog.component';
import {DataService} from '../../../services/data.service';
import {Subject} from 'rxjs';
import {OpportunityChangeBudgetDialogComponent} from '../opportunity-change-budget-dialog/opportunity-change-budget-dialog.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {OpportunityChangeNameDialogComponent} from '../opportunity-name-change-dialog/opportunity-change-name-dialog.component';
import {MatIcon} from '@angular/material/icon';
import {MatMenuItem} from '@angular/material/menu';



@Component({
    selector: 'app-opportunity-contextual-menu',
    imports: [
        MatIcon,
        MatMenuItem
    ],
    template: `
        <button mat-menu-item (click)="openChangeOpportunityNameDialog()">
            <mat-icon>edit</mat-icon>
            Change opportunity name
        </button>

        @if (hasChangeOpportunityLinkRole) {
            <button mat-menu-item (click)="openChangeOpportunityBudgetDialog()">
                <mat-icon>edit</mat-icon>
                Reassign opportunity
            </button>
        }

        @if (!opportunityHasLinkedBudget) {
            <button mat-menu-item
                    (click)="deleteOpportunity()"
            >
                <mat-icon>delete</mat-icon>
                Delete opportunity
            </button>
        }

        @if (userCanAddFollowUps && moreFollowUpsCanBeAdded) {
            <button mat-menu-item
                    (click)="openFollowUpDialog()"
            >
                <mat-icon>add_chart</mat-icon>
                Add follow-up
            </button>
        }

        <button mat-menu-item (click)="openAssignSalesManagerDialog()">
            <mat-icon>badge</mat-icon>
            Change sales manager
        </button>

        @if (!opportunityHasLinkedBudget) {
            <button mat-menu-item (click)="createBudget()">
                <mat-icon>request_page</mat-icon>
                Create budget
            </button>
        }
    `
})
export class OpportunityContextualMenuComponent implements OnDestroy {


    @Input() opportunity: Opportunity;

    private _hasAddFollowUpRole = false;
    private _hasChangeOpportunityLinkRole = false;

    private unsubscribe$ = new Subject();

    constructor(
        private authService: AuthService,
        private matDialog: MatDialog,
        private opportunityService: OpportunityService,
        private dataService: DataService,
        private matSnackbar: MatSnackBar
    ) {

        this.authService.userHasPermissions(
            [
                'app.opportunity.followup.forceadd',
                'app.opportunity.changebudget'
            ])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((val) => {
                this._hasAddFollowUpRole = val[0];
                this._hasChangeOpportunityLinkRole = val[1];
            });
    }

    get userCanAddFollowUps() {
        return this.opportunity.salesManager === this.authService.currentUser.email ||
            this._hasAddFollowUpRole;
    }

    get opportunityHasLinkedBudget() {
        return !!this.opportunity.budgetNr;
    }

    get moreFollowUpsCanBeAdded() {
        return this.opportunity.status === OpportunityStatuses.IN_PROGRESS;
    }

    get hasChangeOpportunityLinkRole(): boolean {
        return this._hasChangeOpportunityLinkRole;
    }

    openChangeOpportunityNameDialog() {
        this.matDialog.open(
            OpportunityChangeNameDialogComponent,
            {disableClose: true, data: this.opportunity}
        ).afterClosed().pipe(
            take(1),
            filter(res => res)
        ).subscribe(newName =>
            this.opportunityService.changeOpportunityName(this.opportunity, newName)
                .then(() => console.log('Name successfully changed!'))
                .catch(error => console.error('Error changing opportunity name', error))
        );
    }

    openChangeOpportunityBudgetDialog() {
        this.matDialog.open(
            OpportunityChangeBudgetDialogComponent,
            {disableClose: true, data: this.opportunity, width: '40vw'}
        ).afterClosed().pipe(
            take(1),
            filter(res => res)
        ).subscribe(async newBudget => {
            if (newBudget) {
                await this.dataService.changeOpportunityBudget(
                    this.opportunity.id,
                    this.opportunity.budgetId,
                    newBudget.id,
                    newBudget.budgetNr);
            }
        });
    }

    openFollowUpDialog() {
        this.matDialog.open(
            FollowUpCreationDialogComponent,
            {disableClose: true, data: this.opportunity}
        );
    }

    deleteOpportunity() {
        this.matDialog.open(
            OpportunityDeletionDialogComponent,
            {disableClose: true, data: this.opportunity}
        ).afterClosed().pipe(
            take(1),
            filter(res => !!res)
        ).subscribe(() =>
            this.opportunityService.deleteOpportunity(this.opportunity)
                .then(() => console.log('opportunity deleted successfully'))
                .catch(error => console.error('error deleting opportunity', error))
        );
    }

    openAssignSalesManagerDialog() {
        this.matDialog.open(
            AssignSalesManagerDialogComponent,
            {disableClose: true}
        ).afterClosed().pipe(
            take(1),
            filter(res => !!res)
        ).subscribe(user =>
            this.opportunityService.assignSalesManager(this.opportunity, user.email)
                .then(() => console.log('Sales manager successfully assigned'))
                .catch(error => console.error('Error assigning sales manager: ', error))
        );
    }

    createBudget() {
        this.opportunityService.setPendingAction();
        this.dataService.getCompanyDetailsById(this.opportunity.companyId).pipe(
            take(1),
            map(r => r['company']),
            switchMap(company => {
                const cosmoId = this.opportunityService.extractCosmoIdFromCompany(company);
                return this.dataService.createBudgetFromOpportunity(this.opportunity, cosmoId);
            })
        ).subscribe((result: any) => {
            console.log('result', result);
            this.opportunityService.unsetPendingAction();
            this.matSnackbar.open(result.message, null, {duration: 5000});
        });
    }

    public ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}
